import React, { Component } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import {
    FlexContainer, CardList, LottieLoadingIndicator,
    Toast,
    BusinessPortalHeader,
    PAYMENT_FLOW_MAX_WIDTH,
    PortalCaption,
    PortalFormActions,
    PortalFormScrollBody,
    PortalFormSection,
    PortalFormSurface,
    PortalPanelHeader,
    PortalPaymentOptionGrid,
    PortalSectionTitle,
    StandardModalActionButton,
    StandardModalActionGroup
} from "src/components";

import Price from "../../Subscription/components/Price";
import "@trendmicro/react-radio/dist/react-radio.css";
import { SubscriptionRenewalContext } from "../SubscriptionRenewalContext";
import { SubscriptionContext } from "src/scenes/Subscription/SubscriptionContext";

import { RenewalForms } from "../../SubscriptionRenewal/SubscriptionRenewal";
import AirwallexForm, { AirwallexProduct } from "src/scenes/Subscription/components/AirwallexForm";
import { withRouter } from "react-router-dom";
import { calculateTotals } from "src/utils/helpers";

import { PaymentSelectionButton, PaymentSelectionIcon, Methods } from "src/scenes/Subscription/forms/Review";

class Renewal extends Component {
    static contextType = SubscriptionRenewalContext;
    state = {
        loadingPurchase: false,
        method: Methods.EXISTING,
        methodManuallyChanged: false
    };

    handleBackButton = () => {
        this.props.history.push("/setting/subscriptions");
    };

    /**
     *  Returns a Promise that is being used to `await` in CardList.js thisPurchaseClicked()
    */
    purchaseRenewal = (selectedMethod, methodSource) => {

        if (methodSource === "") {
            return Toast.error("Please add a new card to continue.");
        } else if (methodSource !== "airwallex") {
            return Toast.error(`Unable to process ${methodSource} payment as of the moment. Please add a new card to continue.`);
        }

        return this.purchaseWithOurApi(
            `${API_URL}/company/airwallex/purchaseNow/renewal`,
            {
                paymentMethodId: selectedMethod
            });
    };

    purchaseWithOurApi = async (url, requestBody) => {
        const { token, nextClick } = this.context;
        this.setState({
            loadingPurchase: true
        });
        axios.post(url, requestBody, {
            headers: {
                Authorization: "Bearer " + token
            }
        }).then(() => {
            this.setState({
                loadingPurchase: false
            });

            nextClick();
        }).catch(error => {
            this.setState({
                loadingPurchase: false
            });
            return Toast.error(error.getApiErrorDetails().message);
        });
    };

    updateMethod = (value) => {
        this.setState({
            method: value,
            methodManuallyChanged: true
        });
    };

    handleCardsLoaded = (cards) => {
        const { methodManuallyChanged } = this.state;

        if (!methodManuallyChanged && (!cards || cards.length === 0)) {
            this.setState({ method: Methods.NEW });
        }
    };

    render() {
        const { loadingCount, token, email, nextClick, currentSubscription,
            totalNumber, companyName, abn, moreUsers, currentForm
        } = this.context;

        const { method, loadingPurchase } = this.state;

        const isUpdatingOutstandingSubscription = currentSubscription && !currentSubscription.isExpired;

        const updatePaymentAndPurchase = () => {
            nextClick();
        };

        const subTotalCents = currentSubscription?.periodNetAmountCents
                     * totalNumber
                     * (currentSubscription?.periodsDue || 0);

        const { gst, processingFee, total: totalAmount } = calculateTotals({
            subtotalCents: subTotalCents,
            taxFraction: currentSubscription?.taxFraction,
            processingRate: currentSubscription?.processingFee
        });

        return (
            <>
                <BusinessPortalHeader
                    title={isUpdatingOutstandingSubscription ? "Outstanding Subscription" : "Reactivate Subscription"}
                    description={`${isUpdatingOutstandingSubscription ? "Your subscription payments are overdue" : "Your business subscription has expired"}, and to continue enjoying our services, you'll need to reactivate this subscription. Please update or enter a new payment method and select 'Pay' to complete the process. Your chosen payment method will be used for future subscription renewals.`}
                    maxWidth={PAYMENT_FLOW_MAX_WIDTH}
                />

                <PageLoadingContainer isLoading={loadingCount > 0 || loadingPurchase} >
                    <PortalFormSurface maxWidth={PAYMENT_FLOW_MAX_WIDTH} marginTop="0" flex="1 1 auto" minHeight="0">
                        <PortalFormScrollBody columns="repeat(2, minmax(0, 1fr))" padding="1.25rem" gap="1rem">
                            <PortalFormSection>
                                <SubscriptionContext.Provider value={{
                                    country: this.context.country,
                                    costing: this.context.costing
                                }} >
                                    <Price
                                        Forms={RenewalForms}
                                        currentForm={currentForm}
                                        companyName={companyName}
                                        totalNumber={totalNumber}
                                        currentSubscription={currentSubscription}
                                        periodsForPayment={currentSubscription?.periodsDue || 0}
                                        goToOverview={ this.handleBackButton }
                                        processingFee={processingFee}
                                        gst={gst}
                                        totalAmount={totalAmount}
                                        hideBackButton
                                    />
                                </SubscriptionContext.Provider>
                            </PortalFormSection>
                            <PortalFormSection>
                                <PortalPanelHeader withDivider>
                                    <div>
                                        <PortalSectionTitle>Payment Method</PortalSectionTitle>
                                        <PortalCaption margin="0.25rem 0 0">
                                            Select an existing payment method or add a new card.
                                        </PortalCaption>
                                    </div>
                                </PortalPanelHeader>

                                <FlexContainer direction="column">
                                    <PortalPaymentOptionGrid>
                                        <PaymentSelectionButton
                                            $active={method === Methods.EXISTING}
                                            onClick={() => this.updateMethod(Methods.EXISTING)}
                                        >
                                            <PaymentSelectionIcon $active={method === Methods.EXISTING} />

                                            Use an existing card
                                        </PaymentSelectionButton>
                                        <PaymentSelectionButton
                                            $active={method === Methods.NEW}
                                            onClick={() => this.updateMethod(Methods.NEW)}
                                        >
                                            <PaymentSelectionIcon $active={method === Methods.NEW} />
                                            Add a new card
                                        </PaymentSelectionButton>
                                    </PortalPaymentOptionGrid>
                                    { method === Methods.NEW ?
                                        <FlexContainer align="start" direction="column" flexGrow="1">
                                            <AirwallexForm
                                                token={token} email={email}
                                                productType={AirwallexProduct.RENEWAL}
                                                companyName={companyName}
                                                companyAbn={abn}
                                                numberOfUsers={moreUsers}
                                                backToPrevious={this.handleBackButton}
                                                nextClick={updatePaymentAndPurchase} />
                                        </FlexContainer>
                                        :
                                        <CardList
                                            token={token}
                                            onBackClicked={this.handleBackButton}
                                            onPurchaseClicked={this.purchaseRenewal}
                                            onDeleteClicked={null}
                                            onCardsLoaded={this.handleCardsLoaded}
                                            hideBackButton
                                            minHeight="auto"
                                        />
                                    }
                                </FlexContainer>

                            </PortalFormSection>
                        </PortalFormScrollBody>
                        <PortalFormActions sticky={false}>
                            <StandardModalActionGroup $pushRight>
                                <StandardModalActionButton type="button" onClick={() => this.handleBackButton()}>
                                    Back
                                </StandardModalActionButton>
                            </StandardModalActionGroup>
                        </PortalFormActions>
                    </PortalFormSurface>
                </PageLoadingContainer>
            </>
        );
    }
}

const PageLoadingContainer = ({ isLoading, children }) => {

    return (
        <>
            {
                isLoading && <FlexContainer
                    justifyContent="center"
                    alignItems="center"
                    color="#fff"
                    style={{
                        overflowX: "auto",
                        maxWidth: PAYMENT_FLOW_MAX_WIDTH,
                        minHeight: "40rem",
                        borderRadius: "7px"
                    }}
                >
                    <LottieLoadingIndicator />
                </FlexContainer>
            }
            { !isLoading && children }
        </>
    );
};

export default withRouter(Renewal);
